import {
    Badge,
    Box,
    Card,
    Group,
    Loader,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
    UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    AdminDashboardEntityStatsRO,
    AdminDashboardSalesPeriodRO,
    AdminDashboardStatsRO,
} from '@routellers/types/dist/openapi/admin/admin-dashboard-stats.ro';
import { AttractionReviewStatusEnum } from '@routellers/types/dist/city-attraction/enum/attraction-review-status.enum';
import { CityQuestStatusEnum } from '@routellers/types/dist/city-quest/enum/city-quest-status.enum';
import { PersonReviewStatusEnum } from '@routellers/types/dist/city-person/enum/person-review-status.enum';
import { ReadyRouteStatusEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-status.enum';
import {
    IconBuildingCommunity,
    IconBuildingStore,
    IconCash,
    IconChevronRight,
    IconMapPin,
    IconMapRoute,
    IconMessageStar,
    IconPackage,
    IconReceipt2,
    IconRoute,
    IconUsers,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { api, apiErrorMessage } from '../api';
import { PageHeader } from '../components/page-header';
import { enumLabel } from '../i18n';
import { formatMoneyMap } from '../utils/portal';

type InboxItem = {
    key: keyof AdminDashboardStatsRO['inbox'];
    to: string;
    icon: typeof IconMapPin;
    color: string;
};

const inboxItems: InboxItem[] = [
    {
        key: 'questsNeedReview',
        to: `/quests?status=${CityQuestStatusEnum.NEED_REVIEW}`,
        icon: IconMapRoute,
        color: 'orange',
    },
    {
        key: 'routesNeedReview',
        to: `/routes?status=${ReadyRouteStatusEnum.NEED_REVIEW}`,
        icon: IconRoute,
        color: 'orange',
    },
    {
        key: 'attractionsNeedReview',
        to: `/attractions?reviewStatus=${AttractionReviewStatusEnum.NEED_REVIEW}`,
        icon: IconMapPin,
        color: 'orange',
    },
    {
        key: 'peopleNeedReview',
        to: `/people?reviewStatus=${PersonReviewStatusEnum.NEED_REVIEW}`,
        icon: IconUsers,
        color: 'orange',
    },
    {
        key: 'reviewsPending',
        to: '/reviews',
        icon: IconMessageStar,
        color: 'yellow',
    },
    {
        key: 'ordersOpen',
        to: '/orders',
        icon: IconPackage,
        color: 'blue',
    },
    {
        key: 'venuesNew',
        to: '/venues',
        icon: IconBuildingStore,
        color: 'cyan',
    },
];

const catalogBlocks: Array<{
    key: 'quests' | 'routes' | 'attractions' | 'people';
    to: string;
    icon: typeof IconMapRoute;
    enumPrefix: string;
}> = [
    {
        key: 'quests',
        to: '/quests',
        icon: IconMapRoute,
        enumPrefix: 'enums.cityQuestStatus',
    },
    {
        key: 'routes',
        to: '/routes',
        icon: IconRoute,
        enumPrefix: 'enums.readyRouteStatus',
    },
    {
        key: 'attractions',
        to: '/attractions',
        icon: IconMapPin,
        enumPrefix: 'enums.attractionReviewStatus',
    },
    {
        key: 'people',
        to: '/people',
        icon: IconUsers,
        enumPrefix: 'enums.personReviewStatus',
    },
];

const Section = ({
    title,
    badge,
    action,
    children,
}: {
    title: string;
    badge?: ReactNode;
    action?: ReactNode;
    children: ReactNode;
}) => (
    <Stack gap='md'>
        <Group justify='space-between' align='center' wrap='nowrap'>
            <Group gap='sm' align='center'>
                <Title order={4}>{title}</Title>
                {badge}
            </Group>
            {action}
        </Group>
        {children}
    </Stack>
);

const SalesCard = ({
    title,
    period,
    purchasesLabel,
    refundedLabel,
}: {
    title: string;
    period: AdminDashboardSalesPeriodRO;
    purchasesLabel: string;
    refundedLabel: string;
}) => (
    <Card padding='lg' withBorder h='100%'>
        <Stack gap='xs' h='100%'>
            <Group justify='space-between' align='flex-start'>
                <Text size='sm' c='dimmed'>
                    {title}
                </Text>
                <ThemeIcon size={28} radius='md' variant='light' color='teal'>
                    <IconCash size={16} />
                </ThemeIcon>
            </Group>
            <Title order={2} mt={4}>
                {formatMoneyMap(period.amountCentsByCurrency)}
            </Title>
            <Text size='sm' c='dimmed' mt='auto'>
                {purchasesLabel}:{' '}
                <Text span fw={600} c='var(--mantine-color-text)'>
                    {period.paidCount}
                </Text>
                {period.refundedCount > 0
                    ? ` · ${refundedLabel}: ${period.refundedCount}`
                    : null}
            </Text>
        </Stack>
    </Card>
);

const EntityCard = ({
    title,
    subtitle,
    to,
    icon: Icon,
    total,
    badges,
}: {
    title: string;
    subtitle?: string;
    to: string;
    icon: typeof IconMapRoute;
    total: number;
    badges?: ReactNode;
}) => (
    <Card
        component={Link}
        to={to}
        padding='lg'
        withBorder
        h='100%'
        style={{ textDecoration: 'none', color: 'inherit' }}
    >
        <Stack gap='md' h='100%'>
            <Group justify='space-between' align='flex-start' wrap='nowrap'>
                <Group gap='sm' align='flex-start' wrap='nowrap' style={{ minWidth: 0 }}>
                    <ThemeIcon size={32} radius='md' variant='light' color='brand' flex='none'>
                        <Icon size={18} />
                    </ThemeIcon>
                    <Box style={{ minWidth: 0 }}>
                        <Text fw={600} lh={1.3}>
                            {title}
                        </Text>
                        {subtitle ? (
                            <Text size='xs' c='dimmed' mt={4} lineClamp={2}>
                                {subtitle}
                            </Text>
                        ) : null}
                    </Box>
                </Group>
                <Title order={3} style={{ flexShrink: 0 }}>
                    {total}
                </Title>
            </Group>
            {badges ? (
                <Group gap={6} wrap='wrap' mt='auto'>
                    {badges}
                </Group>
            ) : null}
        </Stack>
    </Card>
);

export const DashboardPage = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState<AdminDashboardStatsRO | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.adminDashboardApi.stats();
                if (!cancelled) setStats(data);
            } catch (error) {
                if (!cancelled) {
                    notifications.show({ color: 'red', message: apiErrorMessage(error) });
                    setStats(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const inboxRows = useMemo(() => {
        if (!stats) return [];
        return [...inboxItems]
            .map((item) => ({ ...item, count: stats.inbox[item.key] }))
            .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
    }, [stats]);

    const inboxTotal = useMemo(
        () => inboxRows.reduce((sum, row) => sum + row.count, 0),
        [inboxRows],
    );

    return (
        <Stack gap={40}>
            <PageHeader title={t('pages.dashboard.title')} />

            {loading && !stats ? (
                <Group justify='center' py='xl'>
                    <Loader />
                </Group>
            ) : null}

            {stats ? (
                <>
                    <Section
                        title={t('pages.dashboard.sales.title')}
                        action={
                            <UnstyledButton
                                component={Link}
                                to='/purchases'
                                style={{ color: 'var(--mantine-color-dimmed)' }}
                            >
                                <Group gap={6}>
                                    <IconReceipt2 size={16} />
                                    <Text size='sm'>{t('pages.dashboard.sales.openPurchases')}</Text>
                                </Group>
                            </UnstyledButton>
                        }
                    >
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='md'>
                            <SalesCard
                                title={t('pages.dashboard.sales.week')}
                                period={stats.sales.week}
                                purchasesLabel={t('pages.dashboard.sales.purchases')}
                                refundedLabel={t('pages.dashboard.sales.refunded')}
                            />
                            <SalesCard
                                title={t('pages.dashboard.sales.month')}
                                period={stats.sales.month}
                                purchasesLabel={t('pages.dashboard.sales.purchases')}
                                refundedLabel={t('pages.dashboard.sales.refunded')}
                            />
                        </SimpleGrid>
                        {stats.sales.byScopeMonth.length ? (
                            <Group gap='xs' wrap='wrap'>
                                {stats.sales.byScopeMonth.map((row) => (
                                    <Badge key={row.scope} variant='outline' color='teal'>
                                        {enumLabel(t, 'enums.purchaseScope', row.scope)}:{' '}
                                        {formatMoneyMap(row.amountCentsByCurrency)} ({row.paidCount})
                                    </Badge>
                                ))}
                            </Group>
                        ) : null}
                    </Section>

                    <Section
                        title={t('pages.dashboard.inbox.title')}
                        badge={
                            <Badge color={inboxTotal ? 'orange' : 'gray'} variant='light'>
                                {inboxTotal}
                            </Badge>
                        }
                    >
                        <Card withBorder padding={0} style={{ overflow: 'hidden' }}>
                            <Stack gap={0}>
                                {inboxRows.map((item, index) => {
                                    const active = item.count > 0;
                                    return (
                                        <UnstyledButton
                                            key={item.key}
                                            component={Link}
                                            to={item.to}
                                            px='md'
                                            py='sm'
                                            style={{
                                                display: 'block',
                                                borderTop:
                                                    index === 0
                                                        ? undefined
                                                        : '1px solid var(--mantine-color-gray-2)',
                                                opacity: active ? 1 : 0.55,
                                                background: active
                                                    ? undefined
                                                    : 'var(--mantine-color-gray-0)',
                                            }}
                                        >
                                            <Group justify='space-between' wrap='nowrap' gap='md'>
                                                <Group gap='sm' wrap='nowrap' style={{ minWidth: 0 }}>
                                                    <ThemeIcon
                                                        size={32}
                                                        radius='md'
                                                        variant='light'
                                                        color={active ? item.color : 'gray'}
                                                        flex='none'
                                                    >
                                                        <item.icon size={18} />
                                                    </ThemeIcon>
                                                    <Text size='sm' fw={500} truncate>
                                                        {t(
                                                            `pages.dashboard.inbox.items.${item.key}`,
                                                        )}
                                                    </Text>
                                                </Group>
                                                <Group gap='xs' wrap='nowrap' flex='none'>
                                                    <Text
                                                        fw={700}
                                                        size='lg'
                                                        c={active ? undefined : 'dimmed'}
                                                        style={{ fontVariantNumeric: 'tabular-nums' }}
                                                    >
                                                        {item.count}
                                                    </Text>
                                                    <IconChevronRight
                                                        size={16}
                                                        color='var(--mantine-color-dimmed)'
                                                    />
                                                </Group>
                                            </Group>
                                        </UnstyledButton>
                                    );
                                })}
                            </Stack>
                        </Card>
                    </Section>

                    <Section title={t('pages.dashboard.catalog.title')}>
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='md'>
                            <EntityCard
                                title={t('pages.dashboard.catalog.cities')}
                                subtitle={t('pages.dashboard.catalog.citiesReady', {
                                    ready: stats.catalog.citiesReady,
                                    total: stats.catalog.citiesTotal,
                                })}
                                to='/cities'
                                icon={IconBuildingCommunity}
                                total={stats.catalog.citiesTotal}
                            />
                            {catalogBlocks.map((block) => {
                                const entityStats = stats.catalog[block.key];
                                return (
                                    <EntityCard
                                        key={block.key}
                                        title={t(`pages.dashboard.catalog.${block.key}`)}
                                        to={block.to}
                                        icon={block.icon}
                                        total={entityStats.total}
                                        badges={entityStats.byStatus
                                            .filter((row) => row.count > 0)
                                            .sort((a, b) => b.count - a.count)
                                            .map((row) => (
                                                <Badge key={row.status} variant='light' color='gray'>
                                                    {enumLabel(t, block.enumPrefix, row.status)}:{' '}
                                                    {row.count}
                                                </Badge>
                                            ))}
                                    />
                                );
                            })}
                        </SimpleGrid>
                    </Section>
                </>
            ) : null}
        </Stack>
    );
};
